import {
  Box,
  Heading,
  Text,
  VStack,
  Divider,
  Link as ChakraLink,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  OrderedList,
  UnorderedList,
  ListItem,
} from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import HelpPageLayout from './HelpPageLayout'
import { useThemeColors } from '@/hooks/useThemeColors'
import { StepContent } from './HelpStepContent'
import { CaseThreadsManual } from './CaseThreadsManual'
import { ServiceCatalogManual } from './ServiceCatalogManual'

const H = (file: string) => `/assets/img/help/${file}`

export default function UserManualPage() {
  const { textSecondary, border } = useThemeColors()

  return (
    <HelpPageLayout>
      <VStack align="stretch" spacing={6} w="full" maxW="1200px">
        <Box>
          <Heading size="lg" mb={2}>
            User Manual
          </Heading>
          <Text color={textSecondary}>
            How to use Support Admin day to day. This guide covers the shell,{' '}
            <strong>Operations home</strong> (Dashboard), <strong>Incoming Request</strong>,{' '}
            <strong>Tickets</strong>, <strong>Case Threads</strong>, and <strong>Service Catalog</strong>. For
            troubleshooting when something goes wrong, see the{' '}
            <ChakraLink as={RouterLink} to="/faq" color="blue.500">
              FAQ
            </ChakraLink>
            .
          </Text>
        </Box>

        {/* ── Getting around ─────────────────────────────────────────────── */}
        <Box>
          <Heading size="md" mb={2}>
            Getting around
          </Heading>
          <Text color={textSecondary} mb={4}>
            Expand a step for detail and screenshots. Sign-in (login, password reset, 2FA) is handled by
            Auth &amp; Identity — use your organization&apos;s auth URL when you need those screens.
          </Text>

          <Accordion allowMultiple defaultIndex={[0]}>
            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    1. After you sign in — shell layout
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: H('dash-01-operations-home.png'),
                    alt: 'Support Admin after sign-in showing Operations home',
                    caption:
                      'After sign-in: sidebar (modules + pinned User Manual / FAQ), header, and Operations home content.',
                  }}
                >
                  <Text>
                    Support Admin shows a <strong>sidebar</strong> on the left, a <strong>header</strong> on top,
                    and page content in the center. Your default landing page is usually{' '}
                    <strong>Operations home</strong> (<Text as="span" fontFamily="mono" fontSize="sm">/dashboard</Text>).
                  </Text>
                  <Heading as="h4" size="sm">
                    Sidebar
                  </Heading>
                  <OrderedList spacing={2} pl={1}>
                    <ListItem>
                      Scroll the middle list for modules your role can access (for example Incoming Request, Tickets,
                      Case Threads, Service Catalog, Reports). Click a group to expand it, then open a child page.
                    </ListItem>
                    <ListItem>
                      At the <strong>bottom</strong> of the sidebar you always see <strong>User Manual</strong> and{' '}
                      <strong>FAQ</strong> — available to every signed-in user.
                    </ListItem>
                    <ListItem>
                      On a narrow screen, open the menu with the header <strong>menu (hamburger)</strong> button; the
                      same links appear in a drawer, including User Manual and FAQ.
                    </ListItem>
                  </OrderedList>
                  <Heading as="h4" size="sm">
                    Header
                  </Heading>
                  <OrderedList spacing={2} pl={1}>
                    <ListItem>The title shows where you are (for example Dashboard or Incoming Request).</ListItem>
                    <ListItem>
                      Use theme / notifications / avatar controls as available in your deployment. Choose{' '}
                      <strong>Profile</strong> or <strong>Logout</strong> from the user menu when present.
                    </ListItem>
                    <ListItem>
                      The header mail icon (<strong>Unread case threads</strong>) opens{' '}
                      <strong>Case Threads</strong> for case-centered email triage.
                    </ListItem>
                  </OrderedList>
                  <Text color={textSecondary} fontSize="sm">
                    Menus depend on role permissions. If a teammate sees a page you do not, ask an administrator to
                    adjust your role, then sign out and sign in again so navigation refreshes.
                  </Text>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Box>

        {/* ── Dashboard ──────────────────────────────────────────────────── */}
        <Box>
          <Divider borderColor={border} mb={6} />
          <Heading size="md" mb={2}>
            Dashboard (Operations home)
          </Heading>
          <Text color={textSecondary} mb={4}>
            Open <strong>Dashboard</strong> from the sidebar (route{' '}
            <Text as="span" fontFamily="mono" fontSize="sm">
              /dashboard
            </Text>
            ). The page title is <strong>Operations home</strong>. Use it to scan ticket and task health, then jump
            into tickets or reports. There is no create form on this page.
          </Text>

          <Accordion allowMultiple defaultIndex={[0]}>
            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    1. Header, refresh time, and quick links
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: H('dash-01-operations-home.png'),
                    alt: 'Operations home header with quick links',
                    caption:
                      'Operations home: Updated time plus quick links Action queue, Ticket status, and My Tickets.',
                  }}
                >
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Operations home</strong> — page title.
                    </ListItem>
                    <ListItem>
                      <strong>Updated …</strong> — local time of the last successful stats refresh.
                    </ListItem>
                    <ListItem>
                      <strong>Action queue</strong> — scrolls to the Action queue section on this page.
                    </ListItem>
                    <ListItem>
                      <strong>Ticket status</strong> — scrolls to the Ticket status section.
                    </ListItem>
                    <ListItem>
                      <strong>My Tickets</strong> — opens Tickets with the <strong>My</strong> view selected (
                      <Text as="span" fontFamily="mono" fontSize="sm">
                        /tickets
                      </Text>
                      ).
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    2. Action queue and Today&apos;s pulse
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: H('dash-02-action-queue-pulse.png'),
                    alt: 'Action queue and Today pulse cards',
                    caption:
                      'Action queue: Unresolved, Overdue, Due today, Unassigned. Today\'s pulse: Received, Avg first response, Resolution within SLA.',
                  }}
                >
                  <Heading as="h4" size="sm">
                    Action queue
                  </Heading>
                  <Text mb={2}>
                    Section link <strong>Open tickets →</strong> goes to{' '}
                    <Text as="span" fontFamily="mono" fontSize="sm">
                      /tickets
                    </Text>
                    . Cards (most also open Tickets when clicked):
                  </Text>
                  <UnorderedList spacing={2} pl={1} mb={4}>
                    <ListItem>
                      <strong>Unresolved</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Overdue</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Due today</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Unassigned</strong>
                    </ListItem>
                  </UnorderedList>
                  <Heading as="h4" size="sm">
                    Today&apos;s pulse
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Received</strong> — tickets received (today&apos;s volume signal).
                    </ListItem>
                    <ListItem>
                      <strong>Avg first response</strong> — average first-response time (formatted duration).
                    </ListItem>
                    <ListItem>
                      <strong>Resolution within SLA</strong> — percentage meeting resolution SLA.
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    3. Ticket status
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: H('dash-03-ticket-status.png'),
                    alt: 'Ticket status strip',
                    caption:
                      'Ticket status: Open, On Hold (Parts), On Hold (Client / Store), Resolved Incomplete, Closed, Cancelled.',
                  }}
                >
                  <Text mb={2}>
                    Section link <strong>Open tickets →</strong> goes to Tickets. Each card opens Tickets. Labels:
                  </Text>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Open</strong>
                    </ListItem>
                    <ListItem>
                      <strong>On Hold (Parts)</strong> — when count &gt; 0, subtitle shows <strong>Avg hold</strong>;
                      tooltip may also show Max hold.
                    </ListItem>
                    <ListItem>
                      <strong>On Hold (Client / Store)</strong> — same avg / max hold behavior.
                    </ListItem>
                    <ListItem>
                      <strong>Resolved Incomplete</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Closed</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Cancelled</strong>
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    4. Task SLA
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: H('dash-04-task-sla.png'),
                    alt: 'Task SLA section',
                    caption: 'Task SLA: Tasks breached, Within SLA, Response met rate. Link: Task reports.',
                  }}
                >
                  <Text mb={2}>
                    Section link <strong>Task reports ↗</strong> opens{' '}
                    <Text as="span" fontFamily="mono" fontSize="sm">
                      /reports/task-reports
                    </Text>
                    .
                  </Text>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Tasks breached</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Within SLA</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Response met rate</strong> — percentage.
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    5. Charts, agent performance, and report shortcuts
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: H('dash-05-charts-reports.png'),
                    alt: 'Dashboard charts and Reports section',
                    caption:
                      'Ticket source, Open task aging, Today\'s trends, Agent performance (top 3), and Reports quick links.',
                  }}
                >
                  <Heading as="h4" size="sm">
                    Ticket source
                  </Heading>
                  <Text mb={3}>
                    Bar chart of active tickets by source. Subtitle shows active count and period (for example{' '}
                    <em>this week</em>).
                  </Text>
                  <Heading as="h4" size="sm">
                    Open task aging
                  </Heading>
                  <Text mb={2}>Buckets for open tasks:</Text>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      <strong>Fresh (≤24h)</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Aging (≤48h)</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Old (≤72h)</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Critical (73h+)</strong>
                    </ListItem>
                  </UnorderedList>
                  <Heading as="h4" size="sm">
                    Today&apos;s trends
                  </Heading>
                  <Text mb={3}>
                    Line chart: <strong>Today</strong> vs <strong>Yesterday</strong> by hour of day (created date).
                  </Text>
                  <Heading as="h4" size="sm">
                    Agent performance (top 3)
                  </Heading>
                  <Text mb={2}>
                    Table columns: <strong>Agent</strong>, <strong>Open</strong>, <strong>Avg FRT</strong>,{' '}
                    <strong>Load</strong>. Link <strong>View full report ↗</strong> →{' '}
                    <Text as="span" fontFamily="mono" fontSize="sm">
                      /reports/agent-performance
                    </Text>
                    .
                  </Text>
                  <Heading as="h4" size="sm">
                    Reports
                  </Heading>
                  <Text mb={2}>
                    Section link <strong>All reports ↗</strong> →{' '}
                    <Text as="span" fontFamily="mono" fontSize="sm">
                      /reports/overview
                    </Text>
                    . Quick cards:
                  </Text>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>SLA Compliance</strong> — Met vs breached →{' '}
                      <Text as="span" fontFamily="mono" fontSize="sm">
                        /reports/sla-compliance
                      </Text>
                    </ListItem>
                    <ListItem>
                      <strong>Agent Performance</strong> — Full leaderboard →{' '}
                      <Text as="span" fontFamily="mono" fontSize="sm">
                        /reports/agent-performance
                      </Text>
                    </ListItem>
                    <ListItem>
                      <strong>Ticket Productivity</strong> — Handling · daily · monthly →{' '}
                      <Text as="span" fontFamily="mono" fontSize="sm">
                        /reports/ticket-productivity
                      </Text>
                    </ListItem>
                    <ListItem>
                      <strong>SD Agent Productivity</strong> — RAW FD/FP workbook →{' '}
                      <Text as="span" fontFamily="mono" fontSize="sm">
                        /reports/agent-productivity
                      </Text>
                    </ListItem>
                    <ListItem>
                      <strong>Task Reports</strong> — Open, overdue &amp; completion →{' '}
                      <Text as="span" fontFamily="mono" fontSize="sm">
                        /reports/task-reports
                      </Text>
                    </ListItem>
                    <ListItem>
                      <strong>FE Attendance</strong> — Field punch compliance →{' '}
                      <Text as="span" fontFamily="mono" fontSize="sm">
                        /reports/fe-attendance
                      </Text>
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    6. Suggested process flow
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent border={border} textSecondary={textSecondary}>
                  <OrderedList spacing={2} pl={1}>
                    <ListItem>
                      Open <strong>Operations home</strong> after sign-in and note the <strong>Updated</strong> time.
                    </ListItem>
                    <ListItem>
                      Scan <strong>Action queue</strong> for Unresolved / Overdue / Due today / Unassigned work.
                    </ListItem>
                    <ListItem>
                      Use <strong>My Tickets</strong> for your assigned ticket list, or click a queue / status card to
                      open the full Tickets list.
                    </ListItem>
                    <ListItem>
                      Check <strong>Task SLA</strong> and aging charts if you own task follow-up; open Task reports
                      when you need detail.
                    </ListItem>
                    <ListItem>
                      Use <strong>Reports</strong> shortcuts for SLA, productivity, or FE attendance deep dives.
                    </ListItem>
                    <ListItem>
                      For email conversations grouped by client or requestor, open <strong>Case Threads</strong>. For
                      new email / portal / voice intake that still needs acknowledge → parent ticket, go to{' '}
                      <strong>Incoming Request</strong> (next section).
                    </ListItem>
                  </OrderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Box>

        {/* ── Incoming Request ───────────────────────────────────────────── */}
        <Box>
          <Divider borderColor={border} mb={6} />
          <Heading size="md" mb={2}>
            Incoming Request
          </Heading>
          <Text color={textSecondary} mb={4}>
            Open <strong>Incoming Request</strong> (
            <Text as="span" fontFamily="mono" fontSize="sm">
              /incoming-request
            </Text>
            ). This is the intake queue for requests that are not yet (or not fully) converted to parent tickets.
            Email conversations for those requests live in <strong>Case Threads</strong>; use this page to acknowledge
            and create the parent. Stats at the top always reflect all incoming volume — the <strong>From</strong> /{' '}
            <strong>To</strong> date filters apply to the table only.
          </Text>

          <Accordion allowMultiple defaultIndex={[0]}>
            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    1. Stats overview and Available Agents
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshots={[
                    {
                      src: H('ir-01-stats-overview.png'),
                      alt: 'Incoming Request operational and SLA stats',
                      caption:
                        'Stats: Total Open, Total Pending, Total Requests, Acknowledgement Rate, On-Time Ack Rate, Avg Ack Time, plus volume by client / type / source.',
                    },
                    {
                      src: H('ir-09-available-agents.png'),
                      alt: 'Available Agents panel for assigners',
                      caption:
                        'Available Agents (team lead / assigner roles): Name, Total, Active. View All opens Agent Status.',
                    },
                  ]}
                >
                  <Heading as="h4" size="sm">
                    Operational cards
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      <strong>Total Open</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Total Pending</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Total Requests</strong>
                    </ListItem>
                  </UnorderedList>
                  <Heading as="h4" size="sm">
                    Acknowledgement KPIs
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      <strong>Acknowledgement Rate</strong> (%)
                    </ListItem>
                    <ListItem>
                      <strong>On-Time Ack Rate</strong> (%)
                    </ListItem>
                    <ListItem>
                      <strong>Avg Ack Time</strong> (minutes)
                    </ListItem>
                  </UnorderedList>
                  <Heading as="h4" size="sm">
                    Volume analytics
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      <strong>Per Client</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Per Transaction Type</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Per Source</strong>
                    </ListItem>
                  </UnorderedList>
                  <Heading as="h4" size="sm">
                    Available Agents
                  </Heading>
                  <Text>
                    Shown only for roles that can assign support agents. Lists up to 10 agents with the fewest
                    assigned tickets. Columns: <strong>Name</strong>, <strong>Total</strong>, <strong>Active</strong>.
                    Button <strong>View All</strong> opens{' '}
                    <Text as="span" fontFamily="mono" fontSize="sm">
                      /agent-status
                    </Text>
                    . Agents without assign permission do not see this panel.
                  </Text>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    2. Tabs, search, and date filters
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: H('ir-03-filters.png'),
                    alt: 'Incoming Request tabs and filters',
                    caption:
                      'Tabs: All Request, Acknowledge, Trash. Filters: Search, From, To. Column filters for Client and Request Type on the grid.',
                  }}
                >
                  <Heading as="h4" size="sm">
                    Tabs (exact labels)
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      <strong>All Request</strong> — pending / not yet acknowledged (badge = pending count).
                    </ListItem>
                    <ListItem>
                      <strong>Acknowledge</strong> — already acknowledged (badge = acknowledged count).
                    </ListItem>
                    <ListItem>
                      <strong>Trash</strong> — soft-deleted requests (badge = trash count). Drawer is read-only here.
                    </ListItem>
                  </UnorderedList>
                  <Heading as="h4" size="sm">
                    Toolbar filters
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Search</strong> — placeholder:{' '}
                      <em>Search by request #, contact, description...</em> Press Enter or click the search icon.
                    </ListItem>
                    <ListItem>
                      <strong>From</strong> / <strong>To</strong> — date fields that filter the table (
                      <Text as="span" fontFamily="mono" fontSize="sm">
                        date_from
                      </Text>{' '}
                      /{' '}
                      <Text as="span" fontFamily="mono" fontSize="sm">
                        date_to
                      </Text>
                      ). They do <strong>not</strong> change the top stats cards.
                    </ListItem>
                    <ListItem>
                      Column filters on <strong>Client</strong> and <strong>Request Type</strong> (options loaded from
                      the API).
                    </ListItem>
                  </UnorderedList>
                  <Text color={textSecondary} fontSize="sm" mt={2}>
                    Switching tabs clears search and reloads the grid. The page also polls about every 30 seconds for
                    newer pending requests and refreshes the table and stats when one appears.
                  </Text>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    3. Table columns by tab
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshots={[
                    {
                      src: H('ir-02-all-request-tab.png'),
                      alt: 'All Request tab columns',
                      caption:
                        'All Request columns include Request ID, Client, Request Type, Source, Subject, Requested Date, Assigned to, Timer.',
                    },
                    {
                      src: H('ir-07-acknowledge-tab.png'),
                      alt: 'Acknowledge tab columns',
                      caption:
                        'Acknowledge tab adds Acknowledged At, Acknowledged By, Ack SLA, Queue Wait (no Assigned to / Timer).',
                    },
                  ]}
                >
                  <Heading as="h4" size="sm">
                    All Request
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      <strong>Request ID</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Client</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Request Type</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Source</strong> — badge (email, portal, voice, etc.)
                    </ListItem>
                    <ListItem>
                      <strong>Subject</strong> — click to open the triage drawer
                    </ListItem>
                    <ListItem>
                      <strong>Requested Date</strong> — formatted like <em>30 Apr 2025 - 09:30 AM</em>
                    </ListItem>
                    <ListItem>
                      <strong>Assigned to</strong> — name; assigners also get an Assign menu (Unassign + agents)
                    </ListItem>
                    <ListItem>
                      <strong>Timer</strong> — live ack timer or acknowledged-on-time / late text
                    </ListItem>
                  </UnorderedList>
                  <Heading as="h4" size="sm">
                    Acknowledge
                  </Heading>
                  <Text mb={2}>Same core columns as All Request, without Assigned to / Timer, plus:</Text>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      <strong>Acknowledged At</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Acknowledged By</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Ack SLA</strong> — On time or N min late
                    </ListItem>
                    <ListItem>
                      <strong>Queue Wait</strong> — time from ack to parent ticket creation when applicable
                    </ListItem>
                  </UnorderedList>
                  <Heading as="h4" size="sm">
                    Trash
                  </Heading>
                  <Text>
                    Soft-deleted rows. Use row actions to <strong>Restore</strong> or{' '}
                    <strong>Delete Permanently</strong> (permission-gated). Opening Subject shows a read-only drawer.
                  </Text>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    4. Row actions (Acknowledge, Create Ticket, Compose Email, Assign)
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: H('ir-04-acknowledge-modal.png'),
                    alt: 'Save and Acknowledge confirmation modal',
                    caption:
                      'Confirm dialog after Save & Acknowledge: Cancel and Confirm & Acknowledge. The Also create parent ticket checkbox is on the drawer, not in this dialog.',
                  }}
                >
                  <UnorderedList spacing={3} pl={1}>
                    <ListItem>
                      <strong>Save &amp; Acknowledge</strong> (blue check) — only when the row is not yet acknowledged.
                      Opens a confirm modal:
                      <UnorderedList mt={2} spacing={1} pl={1}>
                        <ListItem>
                          Helper text: acknowledge and stop the incoming timer; open the drawer first if you need to
                          edit fields.
                        </ListItem>
                        <ListItem>
                          Checkbox <strong>Also create parent ticket</strong> (checked by default).
                        </ListItem>
                        <ListItem>
                          Buttons: <strong>Cancel</strong>, <strong>Save &amp; Acknowledge</strong>.
                        </ListItem>
                        <ListItem>
                          If create-ticket is on and a parent is created, you navigate to{' '}
                          <Text as="span" fontFamily="mono" fontSize="sm">
                            /tickets/&#123;id&#125;/edit
                          </Text>
                          . Otherwise the list switches to the <strong>Acknowledge</strong> tab.
                        </ListItem>
                      </UnorderedList>
                    </ListItem>
                    <ListItem>
                      <strong>Create Ticket</strong> (green ticket icon) — only when acknowledged and there is no parent
                      ticket yet. Navigates to{' '}
                      <Text as="span" fontFamily="mono" fontSize="sm">
                        /tickets/create
                      </Text>{' '}
                      with the request prefilled.
                    </ListItem>
                    <ListItem>
                      <strong>Compose Email</strong> (mail icon) — available on active tabs (not required on Trash
                      workflow). Opens the compose modal (next step).
                    </ListItem>
                    <ListItem>
                      <strong>Assign to</strong> (user-plus) — assigner roles only; menu lists{' '}
                      <strong>Unassign</strong> plus agents. Hidden when a parent ticket already exists on the row.
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    5. Triage drawer — fields and actions
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: H('ir-05-triage-drawer.png'),
                    alt: 'Ticket request triage drawer',
                    caption:
                      'Triage drawer: metadata, original message, enriched fields (Subject, Description, Contact name, Email, Phone, Priority, Review notes), and footer actions.',
                  }}
                >
                  <Text mb={2}>
                    Open by clicking <strong>Subject</strong>. Pending requests are editable; acknowledged or converted
                    requests lock the enriched form. Trash opens read-only.
                  </Text>
                  <Heading as="h4" size="sm">
                    Metadata (read-only)
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      <strong>Source</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Client</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Submitted</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Contact</strong>
                    </ListItem>
                    <ListItem>
                      When acknowledged: <strong>Acknowledged At</strong>, <strong>Acknowledged By</strong>,{' '}
                      <strong>Ack SLA</strong>
                    </ListItem>
                    <ListItem>Status badges such as Pending / Acknowledged / Trashed</ListItem>
                  </UnorderedList>
                  <Heading as="h4" size="sm">
                    Enriched request (editable when pending)
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      <strong>Subject</strong> — placeholder hint: Request ID | Contact Name | Client
                    </ListItem>
                    <ListItem>
                      <strong>Description</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Contact name</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Email</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Phone</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Priority</strong> — severity options from ticket options
                    </ListItem>
                    <ListItem>
                      <strong>Review notes</strong>
                    </ListItem>
                  </UnorderedList>
                  <Text mb={3} fontSize="sm" color={textSecondary}>
                    Original message / source email and AI confidence may appear when the request has that data.
                    Revision history loads under the form when available.
                  </Text>
                  <Heading as="h4" size="sm">
                    Drawer actions
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Compose Email</strong> — when communication is allowed
                    </ListItem>
                    <ListItem>
                      <strong>Call requestor</strong> menu — <strong>Call via ALICE AI</strong> and{' '}
                      <strong>Call via Vicidial</strong> (not on Trash)
                    </ListItem>
                    <ListItem>
                      <strong>Save &amp; Acknowledge</strong> — confirm dialog with{' '}
                      <strong>Also create parent ticket</strong>; confirm button{' '}
                      <strong>Confirm &amp; Acknowledge</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Create Ticket</strong> — when acknowledged and not yet converted
                    </ListItem>
                    <ListItem>
                      <strong>Delete</strong> / <strong>Delete Permanently</strong> — when your role has delete
                      permission; confirm modal before soft or permanent delete
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    6. Compose Email modal
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: H('ir-06-compose-email.png'),
                    alt: 'Compose Email modal',
                    caption: 'Compose Email: From (helpdesk address), To, Cc, Subject, message body, Send.',
                  }}
                >
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>From</strong> — helpdesk mailbox (shown read-only, for example
                      alshelpdesk@actionlabs.com.ph depending on deployment).
                    </ListItem>
                    <ListItem>
                      <strong>To</strong> — request contact email (display).
                    </ListItem>
                    <ListItem>
                      <strong>Cc</strong> — optional; comma-separated addresses.
                    </ListItem>
                    <ListItem>
                      <strong>Subject</strong> — prefilled from the request; editable.
                    </ListItem>
                    <ListItem>
                      Message body — required before <strong>Send</strong>.
                    </ListItem>
                    <ListItem>
                      Buttons: cancel / close and <strong>Send</strong>.
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    7. Trash and restore
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: H('ir-08-trash-tab.png'),
                    alt: 'Incoming Request Trash tab',
                    caption: 'Trash tab: restore soft-deleted requests or Delete Permanently. Drawer is read-only.',
                  }}
                >
                  <OrderedList spacing={2} pl={1}>
                    <ListItem>
                      From All Request or Acknowledge, use <strong>Delete</strong> in the drawer (if permitted). Confirm
                      moves the request to trash: <em>Move this request to trash? You can restore it later from the
                      Trash tab.</em>
                    </ListItem>
                    <ListItem>
                      Open the <strong>Trash</strong> tab. Use table <strong>Restore</strong> to bring the request back,
                      or <strong>Delete Permanently</strong> (cannot be undone in-app).
                    </ListItem>
                    <ListItem>
                      Permanent delete confirm: <em>Permanently delete this request? This cannot be undone.</em>
                    </ListItem>
                  </OrderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    8. End-to-end process flow
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent border={border} textSecondary={textSecondary}>
                  <OrderedList spacing={2} pl={1}>
                    <ListItem>
                      Open <strong>Incoming Request</strong>. Review stats and (if assigner) Available Agents.
                    </ListItem>
                    <ListItem>
                      Stay on <strong>All Request</strong>. Optionally filter by search, dates, Client, or Request Type.
                    </ListItem>
                    <ListItem>
                      Click <strong>Subject</strong> to open triage. Edit Subject, Description, Contact name, Email,
                      Phone, Priority, and Review notes as needed.
                    </ListItem>
                    <ListItem>
                      Optionally Compose Email or Call requestor (ALICE AI / Vicidial) before acknowledging.
                    </ListItem>
                    <ListItem>
                      Choose <strong>Save &amp; Acknowledge</strong>. Leave <strong>Also create parent ticket</strong>{' '}
                      checked to create and open the parent ticket editor, or uncheck to acknowledge only and finish
                      later from the Acknowledge tab with <strong>Create Ticket</strong>.
                    </ListItem>
                    <ListItem>
                      Assigners may assign or unassign pending requests from the Assigned to column before conversion.
                    </ListItem>
                    <ListItem>
                      Use Trash / Restore only for mistaken intake; prefer permanent delete only when the request must
                      never return.
                    </ListItem>
                  </OrderedList>
                  <Text color={textSecondary} fontSize="sm" mt={3}>
                    After acknowledge-with-create, you land on the ticket <strong>Edit</strong> screen. Field-by-field
                    ticket guidance is in <strong>Tickets</strong> below. Use <strong>Create Ticket</strong> from the
                    Acknowledge tab when you acknowledged without creating a parent.
                  </Text>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Box>

        {/* ── Tickets ────────────────────────────────────────────────────── */}
        <Box>
          <Divider borderColor={border} mb={6} />
          <Heading size="md" mb={2}>
            Tickets
          </Heading>
          <Text color={textSecondary} mb={4}>
            Open <strong>Tickets</strong> (
            <Text as="span" fontFamily="mono" fontSize="sm">
              /tickets
            </Text>
            ). Subtitle on the list: <em>Manage tickets created from acknowledged requests</em>. Use this module to
            create, submit, edit, view, assign, and trash parent tickets — including tickets started from Incoming
            Request.
          </Text>

          <Accordion allowMultiple defaultIndex={[0]}>
            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    1. List page — tabs, search, columns, and Create
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshots={[
                    {
                      src: H('tkt-01-list-all.png'),
                      alt: 'Tickets list All Tickets tab',
                      caption:
                        'Tickets list: All Tickets / My Tickets / (My team) / (Unassigned) / Trash, Create New Ticket, search, and grid columns.',
                    },
                    {
                      src: H('tkt-02-list-my.png'),
                      alt: 'My Tickets tab',
                      caption: 'My Tickets — Agent column is hidden; rows are limited to tickets assigned to you.',
                    },
                  ]}
                >
                  <Heading as="h4" size="sm">
                    Tabs (exact labels)
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      <strong>All Tickets</strong> — full list (badge = total).
                    </ListItem>
                    <ListItem>
                      <strong>My Tickets</strong> — assigned to you (badge = your count). No <strong>Agent</strong>{' '}
                      column.
                    </ListItem>
                    <ListItem>
                      <strong>My team</strong> — Senior Team Lead only; tickets for agents in your reporting hierarchy.
                    </ListItem>
                    <ListItem>
                      <strong>Unassigned</strong> — Team Lead / Senior Team Lead / Web Admin / Developer Account only.
                    </ListItem>
                    <ListItem>
                      <strong>Trash</strong> — soft-deleted tickets.
                    </ListItem>
                  </UnorderedList>
                  <Text mb={2} fontSize="sm" color={textSecondary}>
                    The active tab is remembered in the browser (ticket-view store). Dashboard{' '}
                    <strong>My Tickets</strong> opens this page on the My Tickets tab.
                  </Text>
                  <Heading as="h4" size="sm">
                    Toolbar
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      <strong>Create New Ticket</strong> →{' '}
                      <Text as="span" fontFamily="mono" fontSize="sm">
                        /tickets/create
                      </Text>{' '}
                      (requires create permission).
                    </ListItem>
                    <ListItem>
                      Search placeholder: <em>Search by ticket ID, request ID, subject...</em>
                    </ListItem>
                    <ListItem>
                      Column filter: <strong>Client</strong>
                    </ListItem>
                    <ListItem>
                      If you can delete: <strong>Bulk actions</strong> → Move to trash (or Restore / Delete Permanently
                      on Trash) → <strong>Apply</strong>
                    </ListItem>
                  </UnorderedList>
                  <Heading as="h4" size="sm">
                    Columns
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Ticket ID</strong>, <strong>Severity</strong>, <strong>Status</strong>,{' '}
                      <strong>Ageing</strong>, <strong>Hold Time</strong>, <strong>Client</strong>,{' '}
                      <strong>Agent</strong> (not on My Tickets; may show Auto-assigned / Manual),{' '}
                      <strong>Requested Date</strong>, <strong>Submitted at</strong>, <strong>Request ID</strong>
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    2. Row actions, assign, trash, and open edit/view
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshots={[
                    {
                      src: H('tkt-03-list-unassigned.png'),
                      alt: 'Unassigned tab with Assign menu',
                      caption: 'Unassigned tab — Assign to menu: Unassign, Assign to me, then agents.',
                    },
                    {
                      src: H('tkt-04-list-trash.png'),
                      alt: 'Tickets Trash tab',
                      caption: 'Trash tab — Restore or Delete Permanently (row or bulk).',
                    },
                  ]}
                >
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Draft</strong> tickets (no Submitted at): row <strong>Edit</strong> opens{' '}
                      <Text as="span" fontFamily="mono" fontSize="sm">
                        /tickets/:id/edit
                      </Text>
                      . Opening the row also goes to Edit.
                    </ListItem>
                    <ListItem>
                      <strong>Submitted</strong> tickets: <strong>View Details</strong> →{' '}
                      <Text as="span" fontFamily="mono" fontSize="sm">
                        /tickets/:id/view
                      </Text>
                      . Edit from the View page via <strong>Edit ticket</strong>.
                    </ListItem>
                    <ListItem>
                      Soft-delete: <strong>Trash</strong> (permission-gated). Bulk Move to trash skips already-submitted
                      tickets with a warning when applicable.
                    </ListItem>
                    <ListItem>
                      Trash tab: <strong>Restore</strong> or <strong>Delete Permanently</strong>. Confirm modal:{' '}
                      <strong>Confirm</strong> / <strong>Cancel</strong>.
                    </ListItem>
                    <ListItem>
                      Unassigned / assigners: Agent column <strong>Assign to</strong> menu — <strong>Unassign</strong>,{' '}
                      <strong>Assign to me</strong>, then agent names.
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    3. Create / Edit form — Request, Client &amp; billing, Ticket details
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshots={[
                    {
                      src: H('tkt-form-request.png'),
                      alt: 'Request panel on Create New Ticket',
                      caption:
                        'Request panel: Search request..., Add request / Edit request, and linked request Details.',
                    },
                    {
                      src: H('tkt-form-client-billing.png'),
                      alt: 'Client and billing panel',
                      caption: 'Client & billing: Client, Purchase order, Billing (optional).',
                    },
                    {
                      src: H('tkt-form-ticket-details.png'),
                      alt: 'Ticket details panel',
                      caption:
                        'Ticket details: Subject, Source, Severity, Support Tier, Client Reference Number, Team Assignment, Issue description.',
                    },
                  ]}
                >
                  <Text mb={2}>
                    Create title: <strong>Create New Ticket</strong> — <em>Create a ticket from a request and assign to
                    agents or teams</em>. Edit title: <strong>Edit Ticket — {'{ticket number}'}</strong>.
                  </Text>
                  <Heading as="h4" size="sm">
                    Request
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      Search (create): placeholder <em>Search request...</em> — min. 3 characters by request number,
                      client, or contact.
                    </ListItem>
                    <ListItem>
                      Buttons: <strong>Add request</strong> / <strong>Edit request</strong> open the request drawer.
                    </ListItem>
                    <ListItem>
                      Linked request shows a collapse with <strong>Details</strong> / <strong>Hide</strong> (issue
                      description, categories, company, contact, email, phone, address, schedule, serial, model, source,
                      service, priority, submitted, acknowledged, attachments).
                    </ListItem>
                  </UnorderedList>
                  <Heading as="h4" size="sm">
                    Add / Edit Ticket Request drawer
                  </Heading>
                  <Text mb={2} fontSize="sm">
                    Fields include: <strong>Client</strong>, <strong>Service Type</strong>, <strong>Subcategory</strong>,{' '}
                    <strong>Description</strong>, <strong>Devices Affected</strong>, <strong>Request Source</strong>,{' '}
                    <strong>Preferred Schedule</strong>, <strong>Preferred Contact Date &amp; Time</strong>, optional
                    exact error / dispatch until / contact window / store / site address / work impact / permit time,{' '}
                    <strong>Contact Name</strong>, <strong>Contact Number</strong>, <strong>Contact Email</strong>.
                    Footer: <strong>Create Request</strong> or <strong>Save Changes</strong>.
                  </Text>
                  <Heading as="h4" size="sm">
                    Client &amp; billing
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      <strong>Client</strong> — often read-only when inherited from the linked request; otherwise select.
                    </ListItem>
                    <ListItem>
                      <strong>Purchase order</strong> — Select purchase order.
                    </ListItem>
                    <ListItem>
                      <strong>Billing</strong> — optional; placeholder <em>Not yet determined</em>.
                    </ListItem>
                  </UnorderedList>
                  <Heading as="h4" size="sm">
                    Ticket details
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      <strong>Subject</strong> (required) — often auto-filled from the request.
                    </ListItem>
                    <ListItem>
                      <strong>Source</strong> (required)
                    </ListItem>
                    <ListItem>
                      <strong>Severity</strong> (required) — helper notes Low / Medium / Critical scale.
                    </ListItem>
                    <ListItem>
                      <strong>Support Tier</strong> (optional)
                    </ListItem>
                    <ListItem>
                      <strong>Client Reference Number</strong> (optional)
                    </ListItem>
                    <ListItem>
                      <strong>Team Assignment</strong> — Group / resolver group (optional)
                    </ListItem>
                    <ListItem>
                      Issue description (required) — may show <strong>From request</strong>
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    4. Issue classification, Location, Agent, Notes, Attachments, Tools/parts
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshots={[
                    {
                      src: H('tkt-form-classification.png'),
                      alt: 'Issue classification panel',
                      caption: 'Issue classification: Issue category, Sub-category, Item.',
                    },
                    {
                      src: H('tkt-form-location.png'),
                      alt: 'Location panel',
                      caption: 'Location: Region, Area Cluster, Province, Town.',
                    },
                    {
                      src: H('tkt-form-parts.png'),
                      alt: 'Tools and parts panel',
                      caption: 'Tools / parts needed for this job — name, Qty, Unit, Add part.',
                    },
                    {
                      src: H('tkt-form-attachments.png'),
                      alt: 'Attachments panel',
                      caption: 'Attachments — drag/browse PDF, Word, or images.',
                    },
                    {
                      src: H('tkt-form-agent.png'),
                      alt: 'Agent panel',
                      caption: 'Agent: search by name/email and Assign to me (when permitted).',
                    },
                    {
                      src: H('tkt-form-notes.png'),
                      alt: 'Notes panel',
                      caption: 'Notes — Internal notes... (not visible to the client).',
                    },
                    {
                      src: H('tkt-form-submit.png'),
                      alt: 'Cancel Save as draft Submit buttons',
                      caption: 'Footer: Cancel, Save as draft, and Submit ticket (label varies with dispatch).',
                    },
                  ]}
                >
                  <Heading as="h4" size="sm">
                    Issue classification
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      <strong>Issue category</strong>, <strong>Sub-category</strong>, <strong>Item</strong> (cascade;
                      Clear links per level). Catalog users with create permission may add new values from the form.
                    </ListItem>
                  </UnorderedList>
                  <Heading as="h4" size="sm">
                    Location
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      <strong>Region</strong>, <strong>Area Cluster</strong>, <strong>Province</strong>,{' '}
                      <strong>Town</strong> (dependent selects).
                    </ListItem>
                  </UnorderedList>
                  <Heading as="h4" size="sm">
                    Tools / parts &amp; Attachments
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      Checkbox <strong>Tools / parts needed for this job</strong> — rows with Tool / part name, Qty,
                      Unit; <strong>Add part</strong>.
                    </ListItem>
                    <ListItem>
                      Checkbox <strong>Attachments</strong> — drag/browse PDF, Word, or images.
                    </ListItem>
                  </UnorderedList>
                  <Heading as="h4" size="sm">
                    Agent
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      Search by name or email (min 2 characters), or read-only assigned agent when you cannot reassign.
                    </ListItem>
                    <ListItem>
                      Checkbox <strong>Assign to me</strong> (assigner roles). Reassign is limited to Team Lead, Senior
                      Team Lead, Web Admin, Developer Account.
                    </ListItem>
                  </UnorderedList>
                  <Heading as="h4" size="sm">
                    Notes
                  </Heading>
                  <Text>
                    Placeholder <em>Internal notes...</em> — <strong>not visible to the client</strong>.
                  </Text>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    5. Field engineer dispatch and submit actions
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshots={[
                    {
                      src: H('tkt-form-dispatch.png'),
                      alt: 'FE dispatch Pending readiness',
                      caption:
                        'Requires field engineer dispatch — Dispatch readiness Pending (submit parent without FE Visit). Ready block below stays inactive until Ready.',
                    },
                    {
                      src: H('tkt-form-dispatch-on-hold.png'),
                      alt: 'FE dispatch On Hold fields',
                      caption:
                        'On Hold: Hold reason, Expected follow-up, Remarks — parent → On Hold (Client / Store); no FE Visit.',
                    },
                    {
                      src: H('tkt-form-dispatch-ready.png'),
                      alt: 'FE dispatch Ready with site pin',
                      caption:
                        'Ready: Certification Required / Brand, store/site search, Set dispatch site pin, Confirm location and find engineers, then Primary / Additional FE.',
                    },
                  ]}
                >
                  <Text mb={2} fontSize="sm">
                    The dispatch card unlocks only when <strong>Requires field engineer dispatch</strong> is checked{' '}
                    <em>and</em> a request with a client is linked (Client &amp; billing / linked request). Without a
                    client you only see: <em>Select a client above to configure field engineer dispatch.</em>
                  </Text>

                  <Heading as="h4" size="sm">
                    Checkbox &amp; edit-mode badge
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      <strong>Requires field engineer dispatch</strong> — enables Dispatch readiness and the FE flow.
                    </ListItem>
                    <ListItem>
                      On edit, a task badge may appear for an existing <strong>FE Visit</strong> / coordination task.
                    </ListItem>
                    <ListItem>
                      Edit mode may show <strong>Onsite Coordination</strong> (task number, status, Raw / Paused /
                      Adjusted time) above readiness when coordination is in progress.
                    </ListItem>
                  </UnorderedList>

                  <Heading as="h4" size="sm">
                    Dispatch readiness
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      Segmented control: <strong>Pending</strong> · <strong>On Hold</strong> · <strong>Ready</strong>.
                    </ListItem>
                    <ListItem>
                      <strong>Pending</strong> — helper: parent can be submitted now; no engineer schedule / FE Visit.
                      Submit label stays <strong>Submit ticket</strong>.
                    </ListItem>
                    <ListItem>
                      <strong>On Hold</strong> — required <strong>Hold reason</strong>,{' '}
                      <strong>Expected follow-up</strong> (datetime), optional <strong>Remarks</strong>. Helper: parent
                      moves to <strong>On Hold (Client / Store)</strong>; Ageing pauses; no FE Visit. Submit becomes{' '}
                      <strong>Submit Ticket &amp; Place on Hold</strong>.
                    </ListItem>
                    <ListItem>
                      <strong>Ready</strong> — helper: schedule and Primary FE required; submit creates Parent + FE Visit.
                      Submit becomes <strong>Submit Ticket &amp; Create FE Visit</strong> (or{' '}
                      <strong>Create FE Visit</strong> when the ticket is already submitted).
                    </ListItem>
                  </UnorderedList>

                  <Heading as="h4" size="sm">
                    Ready — FE dispatch flow
                  </Heading>
                  <Text mb={2} fontSize="sm" color={textSecondary}>
                    The block below readiness is active only when Ready is selected (dimmed for Pending / On Hold).
                  </Text>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      <strong>Certification Required</strong> — when checked, choose <strong>Brand</strong> (filters FE
                      recommend by certification).
                    </ListItem>
                    <ListItem>
                      Store / site search: placeholder <em>Search store, address, landmark, city...</em> (client store
                      catalog + map geocode). Optional: <strong>Use store address to geocode dispatch location</strong>.
                    </ListItem>
                    <ListItem>
                      <strong>Set dispatch site pin</strong> — map pin; adjust to the exact site, then{' '}
                      <strong>Confirm location and find engineers</strong>. Until confirmed, recommend stays blocked
                      (“Place / Confirm the site pin…”).
                    </ListItem>
                    <ListItem>
                      After confirm: <strong>Site pin confirmed</strong>, activity schedule (
                      <strong>Activity schedule</strong>, <strong>Support duration</strong>, optional manual end
                      override), ranked FE list, assign <strong>Primary</strong>, optional{' '}
                      <strong>Additional FE</strong> slots, manual search, refresh, and eligibility warnings (Reassign /
                      Remove if an FE is no longer eligible).
                    </ListItem>
                    <ListItem>
                      Ready submit stays disabled until site pin is confirmed, Primary FE is assigned, and invalid
                      assignments are resolved.
                    </ListItem>
                  </UnorderedList>

                  <Heading as="h4" size="sm">
                    Footer buttons
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      <strong>Cancel</strong>
                    </ListItem>
                    <ListItem>
                      <strong>Save as draft</strong>
                    </ListItem>
                    <ListItem>
                      Submit label varies with readiness:{' '}
                      <strong>Submit ticket</strong> · <strong>Submit Ticket &amp; Place on Hold</strong> ·{' '}
                      <strong>Submit Ticket &amp; Create FE Visit</strong> · <strong>Create FE Visit</strong>.
                    </ListItem>
                  </UnorderedList>
                  <Text fontSize="sm" color={textSecondary}>
                    Right-hand <strong>context rail</strong> tracks checklist items (Request linked, Subject, Severity,
                    Description, Agent, FE dispatch readiness) and Ready to submit / N of M complete, plus Primary /
                    Additional FE summaries when assigned.
                  </Text>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    6. View ticket — Overview
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: H('tkt-07-view-overview.png'),
                    alt: 'Ticket view Overview tab',
                    caption:
                      'Ticket view: hero (#number, status, severity), Call requestor, Edit ticket, Overview properties and Assigned team.',
                  }}
                >
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      <strong>Back to tickets</strong>
                    </ListItem>
                    <ListItem>
                      Hero: <strong>#{'{ticket number}'}</strong>, status / Reopened / SLA / severity / assignment
                      badges, subject, client · Store code.
                    </ListItem>
                    <ListItem>
                      <strong>Call requestor</strong> → Call via ALICE AI / Call via Vicidial (needs contact phone).
                    </ListItem>
                    <ListItem>
                      <strong>Edit ticket</strong> → edit route.
                    </ListItem>
                  </UnorderedList>
                  <Heading as="h4" size="sm">
                    Tabs
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      <strong>Overview</strong> — SLA / Status / Severity / Tasks glance; request banner; Ticket details /
                      Location / Store / Tools / Attachments; <strong>Latest activity</strong> snippet (
                      <strong>View all →</strong>); rail: Properties, <strong>Assigned team</strong>,{' '}
                      <strong>Work progress</strong>.
                    </ListItem>
                    <ListItem>
                      <strong>Tasks</strong> — workflow list (see §7). Purple count badge; red breach dot when SLA
                      breached.
                    </ListItem>
                    <ListItem>
                      <strong>Activity &amp; Comments</strong> — ticket thread (see §8). Blue count badge.
                    </ListItem>
                  </UnorderedList>
                  <Text fontSize="sm" color={textSecondary} mb={2}>
                    Deep-link <Text as="span" fontFamily="mono" fontSize="sm">?task=id</Text> opens the Tasks tab and the
                    matching task drawer.
                  </Text>
                  <Heading as="h4" size="sm">
                    Overview extras
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      Breach banner: <strong>Task SLA breached</strong> with <strong>Open task</strong>.
                    </ListItem>
                    <ListItem>
                      <strong>Work progress</strong>: N of M complete; link <strong>View tasks</strong>.
                    </ListItem>
                    <ListItem>
                      Assigned team: task assignees, FE schedules, <strong>Create missing FE Visit tasks</strong>,{' '}
                      <strong>Manage schedule</strong> / <strong>Go to schedule &amp; assign</strong>.
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    7. Tasks tab — list, create, templates, and task drawer
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshots={[
                    {
                      src: H('tkt-view-tasks.png'),
                      alt: 'Tasks tab toolbar and list',
                      caption:
                        'Tasks: Create task, Apply template, Reset workflow, Templates, List/Pipeline, filters, Age in status. Pipeline auto-selects when steps have dependencies.',
                    },
                    {
                      src: H('tkt-view-create-task.png'),
                      alt: 'Create task drawer',
                      caption: 'Create task drawer: From template / Blank task (Title, Status, Assigned to).',
                    },
                    {
                      src: H('tkt-view-task-drawer.png'),
                      alt: 'Task detail drawer',
                      caption:
                        'Task drawer: type badge, Priority, Due date, Status, Assignee, Watchers, tabs, Start / Complete / Skip / Save.',
                    },
                  ]}
                >
                  <Heading as="h4" size="sm">
                    Toolbar
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      <strong>Create task</strong> — open create drawer (template or blank).
                    </ListItem>
                    <ListItem>
                      <strong>Apply template</strong> — apply a multi-step <em>workflow</em> template (or replace
                      existing).
                    </ListItem>
                    <ListItem>
                      <strong>Reset workflow</strong> (when tasks exist) — preview safe vs active tasks; remove safe only
                      or remove all.
                    </ListItem>
                    <ListItem>
                      <strong>Templates</strong> menu → <strong>Manage templates</strong> /{' '}
                      <strong>Create template</strong> (service catalog).
                    </ListItem>
                    <ListItem>
                      View: <strong>List</strong> · <strong>Pipeline</strong> (auto Pipeline when dependencies /
                      workflow steps exist).
                    </ListItem>
                  </UnorderedList>

                  <Heading as="h4" size="sm">
                    Filters
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      Pills: <strong>All</strong> · <strong>Active</strong> · <strong>Blocked</strong> ·{' '}
                      <strong>Breached</strong> · <strong>Completed</strong>.
                    </ListItem>
                    <ListItem>
                      <strong>Age in status</strong>: All · Fresh · Aging · Old · Critical.
                    </ListItem>
                  </UnorderedList>

                  <Heading as="h4" size="sm">
                    List row
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      Complete control (circle / check / skipped). Titles: Mark complete, Completed, Skipped, Blocked by
                      dependency, Completed from the mobile app, parts-not-received warning.
                    </ListItem>
                    <ListItem>
                      Title, status / <strong>Reassigned</strong> / <strong>Skipped</strong> badges, module badges
                      (Store, FE, Assets, Parts, Documents…).
                    </ListItem>
                    <ListItem>
                      Assignee (or Unassigned) · SLA · Blocked · AGE bucket · <strong>View task</strong>.
                    </ListItem>
                    <ListItem>
                      Completing from the list: FE Visit cannot be completed here (mobile lifecycle). Parts procurement
                      stays blocked until linked parts are received / not available. Overdue tasks confirm:{' '}
                      <em>Mark it complete anyway?</em>
                    </ListItem>
                  </UnorderedList>

                  <Heading as="h4" size="sm">
                    Create task drawer
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      Tabs: <strong>From template</strong> · <strong>Blank task</strong>.
                    </ListItem>
                    <ListItem>
                      From template: pick a <em>standalone</em> template (not multi-step workflow). Document collection
                      may ask <strong>Document purpose</strong> (Collection / Validation / Approval). Preview shows
                      resolved defaults.
                    </ListItem>
                    <ListItem>
                      Blank: <strong>Title</strong>* (placeholder Task description), <strong>Status</strong> (Pending /
                      In progress / Awaiting customer response / Cancelled), <strong>Assigned to (user ID)</strong>{' '}
                      optional.
                    </ListItem>
                    <ListItem>
                      Footer: <strong>Close</strong> · <strong>Create task</strong>.
                    </ListItem>
                    <ListItem>
                      Parent-field badges (FE Visit / Tools·parts / Attachments) can open create with source defaults
                      without using this drawer’s template tab.
                    </ListItem>
                  </UnorderedList>

                  <Heading as="h4" size="sm">
                    Apply / reset workflow
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      Apply workflow template: select template → preview steps (Sequential / Parallel) →{' '}
                      <strong>Apply N tasks</strong> or <strong>Replace with selected template</strong> (confirm{' '}
                      <strong>Replace workflow?</strong>).
                    </ListItem>
                    <ListItem>
                      Reset: groups <strong>Safe to remove</strong> vs <strong>Active — will interrupt real work</strong>;
                      remove safe only or remove all anyway.
                    </ListItem>
                  </UnorderedList>

                  <Heading as="h4" size="sm">
                    Task types (drawer badge)
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      <strong>Field Visit</strong> (`fe_visit`) — mobile-driven status; admin: Reassign / Cancel visit /
                      Force close.
                    </ListItem>
                    <ListItem>
                      <strong>Parts Procurement</strong> — Parts tab + checklist; complete gated on part receipt.
                    </ListItem>
                    <ListItem>
                      <strong>Document</strong> — document collection / work permit.
                    </ListItem>
                    <ListItem>
                      Onsite coordination — system task: Coordination tab (hold reason, metrics Raw / Paused / Adjusted);
                      no Start/Complete/Save.
                    </ListItem>
                    <ListItem>
                      Generic <strong>Task</strong> — Start / Mark complete / Skip / Cancel / Reopen / Save.
                    </ListItem>
                  </UnorderedList>

                  <Heading as="h4" size="sm">
                    Task drawer — shared fields &amp; tabs
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      Header: task number + title; type badge; priority; status pill.
                    </ListItem>
                    <ListItem>
                      <strong>Priority</strong>: Low / Medium / High / Critical · <strong>Due date</strong> ·{' '}
                      <strong>Status</strong> (hidden for Field Visit) · <strong>Assignee</strong> (search min 2 chars)
                      · <strong>Watchers</strong>.
                    </ListItem>
                    <ListItem>
                      Field Visit tabs: Visit timeline · More details · Dependencies · Expenses (if applicable) ·
                      Activity.
                    </ListItem>
                    <ListItem>
                      Parts: Parts · More details · Checklist · Dependencies · Activity · Attachments.
                    </ListItem>
                    <ListItem>
                      Generic / document: Details · Checklist · Dependencies · Activity · Attachments (may be hosted by
                      document module).
                    </ListItem>
                    <ListItem>
                      Checklist: Add item, Required, attach checklist template (Append / Replace).
                    </ListItem>
                    <ListItem>
                      Dependencies: Blocked by / Blocking · Add dependency (sibling task).
                    </ListItem>
                    <ListItem>
                      Task Activity: placeholder <em>Add a task update or internal note…</em>; checkbox{' '}
                      <strong>Internal note</strong>; <strong>Post</strong>; 2000-char limit. Separate from ticket
                      Activity &amp; Comments.
                    </ListItem>
                    <ListItem>
                      Attachments: <strong>Upload</strong> on the task (ticket Overview also has ticket-level files).
                    </ListItem>
                  </UnorderedList>

                  <Heading as="h4" size="sm">
                    Task drawer — actions
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      Generic: <strong>Start task</strong> · <strong>Mark complete</strong> ·{' '}
                      <strong>Skip — not applicable</strong> (reason) · <strong>Cancel task</strong> (reason) ·{' '}
                      <strong>Reopen</strong> · <strong>Close</strong> · <strong>Save changes</strong>.
                    </ListItem>
                    <ListItem>
                      Field Visit: <strong>Reassign engineer</strong> · <strong>Cancel visit</strong> ·{' '}
                      <strong>Force close</strong> · Save / Close (no list-side complete).
                    </ListItem>
                    <ListItem>
                      Superseded visit: info <strong>Reassigned visit</strong> + <strong>Open replacement visit</strong>.
                    </ListItem>
                    <ListItem>
                      Completion preflight can block complete (dependencies, checklist, parts) with listed messages.
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    8. Activity &amp; Comments
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent
                  border={border}
                  textSecondary={textSecondary}
                  screenshot={{
                    src: H('tkt-view-activity.png'),
                    alt: 'Activity and Comments tab',
                    caption:
                      'Activity & Comments: All / Comments / System filters, thread, Write a comment…, Internal note, Post.',
                  }}
                >
                  <Heading as="h4" size="sm">
                    Feed filters
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      <strong>All</strong> · <strong>Comments</strong> · <strong>System</strong> — plus item count.
                    </ListItem>
                    <ListItem>
                      Empty: <em>No activity or comments yet. Be the first to post.</em> / filter miss:{' '}
                      <em>No items in this filter.</em>
                    </ListItem>
                  </UnorderedList>

                  <Heading as="h4" size="sm">
                    Compose
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      Placeholder: <em>Write a comment…</em>
                    </ListItem>
                    <ListItem>
                      Checkbox: <strong>Internal note (not visible to customer)</strong> — posts as type{' '}
                      <code>note</code>; unchecked posts as <code>comment</code>.
                    </ListItem>
                    <ListItem>
                      <strong>Post</strong> (or Ctrl/Cmd+Enter). Max <strong>2000</strong> characters (counter shown).
                    </ListItem>
                    <ListItem>
                      Toasts: Comment added / Internal note added / Reply posted.
                    </ListItem>
                  </UnorderedList>

                  <Heading as="h4" size="sm">
                    Thread actions
                  </Heading>
                  <UnorderedList spacing={2} pl={1} mb={3}>
                    <ListItem>
                      <strong>Reply</strong> — nested composer (<em>Reply to {'{name}'}…</em>),{' '}
                      <strong>Post reply</strong> / <strong>Cancel</strong> (max depth 12).
                    </ListItem>
                    <ListItem>
                      Edit own posts — pencil opens <strong>Edit Update</strong> (<em>Type your update here…</em>,{' '}
                      <strong>Save</strong>).
                    </ListItem>
                    <ListItem>
                      System rows (e.g. status change) appear under <strong>System</strong> / All — not customer
                      comments.
                    </ListItem>
                  </UnorderedList>

                  <Heading as="h4" size="sm">
                    What is not here
                  </Heading>
                  <UnorderedList spacing={2} pl={1}>
                    <ListItem>
                      No file upload on this tab — ticket attachments live on Overview; task files in the task drawer.
                    </ListItem>
                    <ListItem>
                      Task-level notes use the task drawer <strong>Activity</strong> tab (separate feed).
                    </ListItem>
                    <ListItem>
                      Overview <strong>Latest activity</strong> is a short preview with <strong>View all →</strong> into
                      this tab.
                    </ListItem>
                  </UnorderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem borderColor={border}>
              <h3>
                <AccordionButton py={3} px={2}>
                  <Box as="span" flex="1" textAlign="left" fontWeight="semibold">
                    9. Process flows (Incoming → Ticket and day-to-day)
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={5} px={2}>
                <StepContent border={border} textSecondary={textSecondary}>
                  <Heading as="h4" size="sm">
                    From Incoming Request
                  </Heading>
                  <OrderedList spacing={2} pl={1} mb={4}>
                    <ListItem>
                      <strong>Save &amp; Acknowledge</strong> with <strong>Also create parent ticket</strong> → API
                      creates parent → navigate to <strong>Edit</strong>.
                    </ListItem>
                    <ListItem>
                      Or acknowledge without create → later use <strong>Create Ticket</strong> on Acknowledge tab →{' '}
                      <strong>Create New Ticket</strong> hydrated from the request.
                    </ListItem>
                  </OrderedList>
                  <Heading as="h4" size="sm">
                    Create from scratch
                  </Heading>
                  <OrderedList spacing={2} pl={1} mb={4}>
                    <ListItem>
                      Tickets → <strong>Create New Ticket</strong>.
                    </ListItem>
                    <ListItem>Link or Add request → Client &amp; billing → Ticket details → classification → location.</ListItem>
                    <ListItem>Optional: FE dispatch, tools/parts, attachments.</ListItem>
                    <ListItem>Assign agent → Save as draft or Submit (variant per dispatch readiness).</ListItem>
                    <ListItem>Land on View after successful submit.</ListItem>
                  </OrderedList>
                  <Heading as="h4" size="sm">
                    Edit / trash
                  </Heading>
                  <OrderedList spacing={2} pl={1}>
                    <ListItem>Drafts: Edit from list. Submitted: View → Edit ticket.</ListItem>
                    <ListItem>Trash / Restore / Delete Permanently as on the list tabs.</ListItem>
                  </OrderedList>
                </StepContent>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Box>

        <CaseThreadsManual border={border} textSecondary={textSecondary} />

        <ServiceCatalogManual border={border} textSecondary={textSecondary} />

        <Box>
          <Divider borderColor={border} mb={4} />
          <Text color={textSecondary} fontSize="sm">
            For break/fix help with symptoms and recovery steps, open the{' '}
            <ChakraLink as={RouterLink} to="/faq" color="blue.500">
              FAQ
            </ChakraLink>
            .
          </Text>
        </Box>
      </VStack>
    </HelpPageLayout>
  )
}
